// ===========================================================================
// Payout batches — paid timeline (InsureDocs v5.9.1)
//
// Pure helpers (no React, no API calls) that merge PAID payout batches with
// one-time bonuses into one newest-first list for the Payout batches page.
// One-time bonuses stay one-time bonuses — these are display rows only.
//
// Order: paid date, newest first. A batch's paid date is its paid_at as a
// Central (America/Chicago) calendar date; a one-time bonus's is paid_date.
// Same day: batches first (paid_at newest first), then one-time bonuses
// (most recently entered first, by created_at).
// ===========================================================================

const CENTRAL_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
});

// Timestamp (ISO string / Date) -> 'YYYY-MM-DD' in Central time; '' if missing/invalid.
export function toCentralDate(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return CENTRAL_DATE.format(d);
}

const millis = (ts) => {
  const n = ts ? Date.parse(ts) : NaN;
  return Number.isNaN(n) ? 0 : n;
};

// -> [{ kind: 'batch' | 'oneoff', key, date: 'YYYY-MM-DD', data }], newest first.
export function mergeTimeline(batches, oneOffs) {
  const items = [
    ...(batches || []).map(b => ({
      kind: 'batch', key: `batch-${b.id}`, date: toCentralDate(b.paid_at), ts: millis(b.paid_at), data: b,
    })),
    ...(oneOffs || []).map(o => ({
      kind: 'oneoff', key: `oneoff-${o.id}`, date: String(o.paid_date || '').slice(0, 10), ts: millis(o.created_at), data: o,
    })),
  ];
  items.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;   // newer date first ('' = unknown sorts last)
    if (a.kind !== b.kind) return a.kind === 'batch' ? -1 : 1; // same day: batches first
    if (a.ts !== b.ts) return b.ts - a.ts;                     // batches by paid_at, one-offs by created_at
    return (b.data.id || 0) - (a.data.id || 0);                // stable tie-break
  });
  return items.map(({ ts, ...rest }) => rest);
}

// Browser-side paging. page is 0-based and clamped into range.
export function pageSlice(items, page, size = 20) {
  const list = items || [];
  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const p = Math.min(Math.max(0, page || 0), pages - 1);
  return {
    rows: list.slice(p * size, p * size + size),
    page: p,
    pages,
    total,
    hasPrev: p > 0,
    hasNext: p < pages - 1,
  };
}
